import { useShowModalResult } from "@/components/modal-result";
import { updateProfile } from "@/hooks/useUsers";
import useAuthStore from "@/store/useAuthStore";
import { LinkedinFilled } from "@ant-design/icons";
import { Button, Card, Form, Input, Modal, notification } from "antd";
import { useEffect, useState } from "react";
import { HiPencil } from "react-icons/hi";
import linkedInIcon from "@/assets/linkedin-icon.png";
import { FaLinkedin } from "react-icons/fa";
import Link from "next/link";
import Spinner from "@/components/Spinner";
import { capitalize } from "lodash";

interface IProfileWidgetLinkedIn {
  refetchUserData: () => Promise<any>;
  isLoadingFetch: boolean;
  linkedInData: string | null;
  _version: string | number;
  userId: string;
  isVisitProfile?: boolean;
  visitProfileName?: string;
}

export default function ProfileWidgetLinkedIn({
  refetchUserData,
  isLoadingFetch,
  linkedInData,
  _version,
  userId,
  isVisitProfile,
  visitProfileName,
}: IProfileWidgetLinkedIn) {

  const linkedinRegex =
    /^(https?:\/\/)?(www\.)?linkedin\.com\/(in|pub|company)\/[A-Za-z0-9-]+\/?$/;

  const [isModalLinkedinOpened, isModalLinkedinOpenedSet] = useState(false);
  const [isLoadingSave, isLoadingSaveSet] = useState(false);

  const { successModal } = useShowModalResult();

  const [form] = Form.useForm();
  const watch = Form.useWatch([], form);

  function onCloseModal() {
    isModalLinkedinOpenedSet(false);
  }

  async function onSave() {
    await form.validateFields();

    try {
      isLoadingSaveSet(true);
      await updateProfile({
        id: userId,
        _version: _version as string,
        socialMedia: {
          linkedIn: form.getFieldValue("linkedIn"),
        },
      });
      isLoadingSaveSet(false);
      successModal({
        show: true,
        title: watch["linkedIn"] ? "LinkedIn Profile Connected" : "LinkedIn Removed",
        body: watch["linkedIn"] ? "Your LinkedIn profile has been successfully connected! Your professional network is now more accessible from your profile" : "Your LinkedIn URL has been successfully removed and will no longer appear on your profile.",
        type: "success",
      });
      isModalLinkedinOpenedSet(false);
      await refetchUserData();
    } catch (error: any) {
      isLoadingSaveSet(false);
      notification.error(error?.message);
      console.log(error?.message);
    }
  }

  useEffect(() => {
    if (linkedInData) {
      form.setFieldValue("linkedIn", linkedInData);
    }
  }, [linkedInData]);

  return (
    <>
      <Card
        style={{height: 140}}
        title={
          <div className="flex w-full items-center justify-between">
            <h3 className="font-bold text-lg text-white m-0">{!isVisitProfile ? "My " : ""}Social Media</h3>
            {linkedInData && !isVisitProfile && (
              <Button
                type="text"
                icon={<HiPencil />}
                onClick={() => isModalLinkedinOpenedSet(true)}
              />
            )}
          </div>
        }
      >
        {isLoadingFetch ? (
          <Spinner />
        ) : !linkedInData ? (
          isVisitProfile ? 
            <p>Connecting social media account helps to earns points.</p>
          :
          <Button
            icon={<LinkedinFilled />}
            type="primary"
            onClick={() => isModalLinkedinOpenedSet(true)}
          >
            Connect to LinkedIn
          </Button>
        ) : (
          <Link
            href={!linkedInData?.includes("https://") ? `https://${linkedInData}` : linkedInData}
            target="_blank"
            className="flex items-center gap-2"
          >
            <FaLinkedin className="text-blue-700 bg-white text-xl" />
            <span className="text-yellow font-bold capitalize">{isVisitProfile ? `${visitProfileName}'s LinkedIn` : "My LinkedIn"}</span>
          </Link>
        )}
      </Card>

      <Modal
        open={isModalLinkedinOpened}
        onCancel={onCloseModal}
        footer={[
          <Button
            onClick={onSave}
            type="primary"
            loading={isLoadingSave}
            disabled={!linkedInData && !watch?.["linkedIn"]}
          >
           {linkedInData && !watch?.["linkedIn"] ? "Remove LinkedIn" : "Save"}
          </Button>,
        ]}
        title="Connect to LinkedIn"
      >
        <div className="pt-4">
          <Form layout="vertical" form={form}>
            <Form.Item
              name="linkedIn"
              label="LinkedIn URL"
              required={!linkedInData}
              rules={[
                {
                  pattern: linkedinRegex,
                  message:
                    "Please enter a valid LinkedIn profile URL (e.g., www.linkedin.com/in/your-profile)",
                },
              ]}
            >
              <Input placeholder="Enter LinkedIn URL" />
            </Form.Item>
          </Form>
        </div>
      </Modal>
    </>
  );
}
